// src/lib/pto/days.ts
import { addDays, dayOfWeek, type IsoDate } from "@/lib/time/dates";

/** Day counting shared by the screens; the gateway SQL is the authority (spec R1, R2). */

export function isWeekday(d: IsoDate): boolean {
  const w = dayOfWeek(d);
  return w !== 0 && w !== 6;
}

/**
 * Weekdays start..end minus `bookedHolidays`, the person's active holiday bookings from OTHER
 * requests (as built: every type skips them, holiday-type included; a request's own bookings
 * are excluded from the set by the caller, so they never reduce its days).
 */
export function workingDates(
  start: IsoDate,
  end: IsoDate,
  bookedHolidays: ReadonlySet<IsoDate>,
): IsoDate[] {
  const out: IsoDate[] = [];
  for (let d = start; d <= end; d = addDays(d, 1)) {
    if (isWeekday(d) && !bookedHolidays.has(d)) out.push(d);
  }
  return out;
}

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function dayCount(workDays: number, hoursPerDay: number): number {
  return round2((workDays * hoursPerDay) / 8);
}

/** "1 day", "0.5 day", "2 days", "1.5 days". */
export function formatDayCount(n: number): string {
  const v = round2(n);
  return v <= 1 ? `${v} day` : `${v} days`;
}

/** "4 hrs" for partial days; null for a full day. */
export function formatPartialHours(hoursPerDay: number): string | null {
  return hoursPerDay >= 8 ? null : `${round2(hoursPerDay)} hrs`;
}

const PTO_TYPE_LABELS: Record<string, string> = {
  vacation: "Vacation",
  sick: "Sick",
  holiday: "Holiday",
  other: "Exceptional",
  unpaid: "Unpaid",
};

export function typeLabel(typeEnum: string, typeName: string): string {
  return PTO_TYPE_LABELS[typeEnum] ?? typeName;
}
